import React from 'react';
import { motion } from 'framer-motion';
import { Target, Palette, Globe, TrendingUp, ShoppingBag, Rocket, Zap, ArrowRight } from 'lucide-react';
import { services } from '../mockData';

const iconMap = {
  Target,
  Palette,
  Globe,
  TrendingUp,
  ShoppingBag,
  Rocket,
  Zap
};

const Services = () => {
  return (
    <section id="services" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 px-4"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 mb-4">
            Our Services
          </h2>
          <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 mx-auto mb-4 sm:mb-6 neon-glow"></div>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            Built for Growth
          </p>
        </motion.div>

        {/* Services Grid */}
        <div
          data-testid="services-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:[&>*:last-child:nth-child(3n-2)]:col-start-2"
        >
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.2 }
                }}
                className="group relative"
              >
                <div className="relative h-full p-6 sm:p-8 bg-gradient-to-br from-slate-900/80 to-slate-800/80 rounded-2xl border border-cyan-500/20 hover:border-cyan-500/60 transition-all duration-300 backdrop-blur-sm overflow-hidden holographic">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-purple-600/0 group-hover:from-cyan-500/10 group-hover:to-purple-600/10 transition-all duration-300"></div>
                  
                  {/* Icon */}
                  <div className="relative mb-4 sm:mb-6">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center neon-glow-icon group-hover:scale-110 transition-transform duration-300">
                      <Icon size={28} className="text-white sm:w-8 sm:h-8" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 group-hover:text-cyan-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-4 sm:mb-6">
                      {service.description}
                    </p>
                    <button className="flex items-center gap-2 text-cyan-400 font-semibold group-hover:gap-4 transition-all">
                      Learn More
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>

                  {/* Corner accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
